import { Box } from '@mui/material';
import { useDrop } from 'react-dnd';
import type { DropTargetMonitor } from 'react-dnd';
import { useState, useEffect, useRef, useMemo } from 'react';
import ShortUniqueId from 'short-unique-id';
import { NotificationDialog } from '../../CommonFunctions/DialogComponents';
import { TAlert } from '../../../types/types';
import MeasureCard from './MeasureCard';
import MeasureMapping from './MeasureMapping';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateMeasureDropFilters,
  removeMeasureFieldCard,
  removeMeasureMappingColumn,
  callQueryAPI,
} from '../../../redux/ChartPoperties/ChartControlsActions';
import { fetchUniqueOptions } from '../../../api/filters';
import { RootState } from '../../../redux';

interface IDropZone {
  propKey: string;
  chartControls: any;
  chartProperties: any;
}

interface MeasureCardData {
  id: string;
  name: string;
}

interface DragItem {
  type: 'card' | 'calculation';
  boxUUID?: string;
  bIndex?: number;
  fieldData?: {
    dataType?: string;
    fieldname?: string;
    displayname?: string;
    tableId?: string;
    agg?: string;
  };
}

interface CollectedProps {
  isOver: boolean;
  canDrop: boolean;
}

const MeasureDropZone = ({ propKey, chartControls, chartProperties }: IDropZone) => {
  const [alert, setAlert] = useState<null | TAlert>(null);
  const currentOperator =
    chartControls?.properties[propKey]?.columnSecurity?.currentOperator?.toLowerCase();
  const [cards, setCards] = useState<MeasureCardData[]>([]);

  const uid = new ShortUniqueId({ length: 4 });
  const dispatch = useDispatch();
  const uniqueId = uid();
  const datasetId = chartProperties.properties[propKey].selectedDs.id;
  const dbConnectionId = chartProperties.properties[propKey].selectedDs.connectionId;
  const { workspaceId, calculations } = useSelector((state: RootState) => ({
    workspaceId: state.tabTileProps?.selectedDataSetList?.[0]?.workSpaceId,
    calculations: state.calculations,
  }));

  // const isFirstRender = useRef(true);

  const measureMappingRef = useRef(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMapping, setSelectedMapping] = useState<Record<string, string>[]>(
    chartControls?.properties?.[propKey]?.columnSecurity?.filterPanels?.[currentOperator]
      ?.filters?.[1]?.mapping ?? [],
  );
  const [responseData, setResponseData] = useState<(string | number | null)[] | null>(null);

  const [showPopover, setShowPopover] = useState(false);

  const savedCalculations = useMemo(
    () => calculations?.savedCalculations,
    [calculations?.savedCalculations],
  );

  const savedMappings: Record<string, string>[] =
    chartControls?.properties?.[propKey]?.columnSecurity?.filterPanels?.[currentOperator]
      ?.filters?.[1]?.mapping ?? [];

  const measureFields = useMemo(() => {
    const axes = chartProperties.properties?.[propKey]?.chartAxes || [];

    return axes
      .filter((axis: any) => ['X Axis', 'Y Axis', 'Measure'].includes(axis.name))
      .flatMap((axis: any) => axis.fields || []);
  }, [chartProperties.properties?.[propKey]?.chartAxes]);

  const extraMeasureFields = useMemo(() => {
    const extraFields =
      chartControls.properties?.[propKey]?.formatOptions?.tooltipFormats?.extraMeasureField || [];

    return extraFields?.[0]?.fields || [];
  }, [chartControls.properties?.[propKey]?.formatOptions?.tooltipFormats?.extraMeasureField]);

  const updatedFilter = {
    ...(chartControls?.properties?.[propKey]?.columnSecurity?.filterPanels?.[
      currentOperator.toLowerCase()
    ]?.filters?.[1] ?? {}),
    filterOption: 'allValues',
  };

  const getCalculationInfoByUid = (uid: string) => {
    if (!savedCalculations || !uid) return null;

    return savedCalculations.find((item: any) => item.uuid === uid);
  };

  useEffect(() => {
    //Removing extra measure mappings on chart switch or on measure/extra-measure axis change due to drag and drop
    const measureFieldsUIds = measureFields.map((field: any) => field.uId);
    const extraMeasureFieldsUIds = extraMeasureFields.map((field: any) => field.uId);
    const allowedUIds = new Set([...measureFieldsUIds, ...extraMeasureFieldsUIds]);
    savedMappings.forEach((mapping) => {
      if (!allowedUIds.has(Object.keys(mapping)[0])) {
        dispatch(removeMeasureMappingColumn(propKey, currentOperator, Object.keys(mapping)[0]));
      }
    });
  }, [measureFields, extraMeasureFields]);

  useEffect(() => {
    if (showPopover && measureMappingRef.current) {
      setAnchorEl(measureMappingRef.current);
    }
  }, [cards, showPopover]);

  // Measure Mapping for a particular measure is deleted if that measure gets removed from measure axis or from extra measure axis. So to reflect that change here useEffect is used.
  useEffect(() => {
    setSelectedMapping(
      chartControls?.properties?.[propKey]?.columnSecurity?.filterPanels?.[currentOperator]
        ?.filters?.[1]?.mapping ?? [],
    );
  }, [
    chartControls?.properties?.[propKey]?.columnSecurity?.filterPanels?.[currentOperator]
      ?.filters?.[1]?.mapping,
  ]);

  useEffect(() => {
    const fetchData = async () => {
      if (
        (datasetId || dbConnectionId) &&
        workspaceId &&
        updatedFilter &&
        chartControls?.properties?.[propKey]?.columnSecurity?.filterPanels?.[
          currentOperator.toLowerCase()
        ]?.filters?.[1]
      ) {
        const calculation = getCalculationInfoByUid(updatedFilter.calculationUUID);
        if (calculation) {
          updatedFilter.calculatedField = [
            {
              calculatedFieldName: calculation.name.split(' ').join(' '),
              isAggregated: calculation.isAggregated,
              fields: calculation.calculationInfo.fields,
              flows: calculation.calculationInfo.flows,
              conditionFilters: calculation.calculationInfo.conditionFilters,
              overrided: calculation.calculationInfo.overrideCount > 0,
            },
          ];
          updatedFilter.isCalculatedField = true;
        }
        const response = await fetchUniqueOptions(
          dbConnectionId,
          datasetId,
          workspaceId,
          updatedFilter,
        );
        setResponseData(response.data);
        console.log(response);
      }
    };
    fetchData();
  }, [
    datasetId,
    dbConnectionId,
    workspaceId,
    chartControls?.properties?.[propKey]?.columnSecurity?.filterPanels?.[
      currentOperator.toLowerCase()
    ]?.filters?.[1]?.displayName,
  ]);

  useEffect(() => {
    const filterObj =
      chartControls?.properties?.[propKey]?.columnSecurity?.filterPanels?.[currentOperator]
        ?.filters?.[1];

    const cardsFromRedux = filterObj
      ? [
          {
            id: filterObj.source,
            name: filterObj.displayName,
          },
        ]
      : [];

    setCards(cardsFromRedux);
  }, [chartControls, propKey, currentOperator]);

  const chartAxes = chartProperties.properties[propKey]?.chartAxes;
  let dimensions: any[] = [];
  let measures: any[] = [];
  let filters: any[] = [];

  // Loop through chartAxes
  chartAxes.forEach((axis: any) => {
    if (['Row', 'Column', 'Dimension'].includes(axis.name)) {
      const filteredFields = (axis.fields || []).map((field: any) => ({
        dataType: field.dataType,
        fieldName: field.fieldname,
        tableId: field.tableId,
      }));
      dimensions = dimensions.concat(filteredFields);
    }

    if (['X Axis', 'Y Axis', 'Measure'].includes(axis.name)) {
      const filteredFieldsMeasure = (axis.fields || []).map((field: any) => ({
        dataType: field.dataType,
        displayName: field.displayname,
        fieldName: field.fieldname,
        tableId: field.tableId,
        aggr: field.agg,
        measureId: field.uId,
      }));
      measures = measures.concat(filteredFieldsMeasure);
    }

    if (axis.name === 'Filter') {
      const filteredFields = (axis.fields || []).map((field: any) => ({
        tableId: field.tableId,
        displayName: field.displayname,
        fieldName: field.fieldname,
        dataType: field.dataType,
      }));
      filters = filters.concat(filteredFields);
    }
  });

  // useEffect(() => {
  //   if (isFirstRender.current) {
  //     isFirstRender.current = false;
  //     return;
  //   }
  //   if (
  //     chartControls?.properties?.[propKey]?.columnSecurity?.filterPanels?.[
  //       currentOperator.toLowerCase()
  //     ]?.filters?.[1]?.mapping?.length > 0
  //   ) {
  //     dispatch(callQueryAPI(propKey, true));
  //   }
  //   // fetchData();
  // }, [
  //   chartProperties.properties[propKey].chartAxes,
  //   chartProperties.properties[propKey].chartType,
  //   // chartProperties.properties[propKey].axesEdited,
  //   chartControls.properties[propKey]?.columnSecurity?.filterPanels[currentOperator]?.filters,
  //   chartControls.properties[propKey]?.columnSecurity?.filterPanels[currentOperator],
  // ]);

  const [{ isOver, canDrop }, drop] = useDrop<DragItem, void, CollectedProps>({
    accept: ['card', 'calculation'],
    canDrop: (item) => item.bIndex === 99,
    collect: (monitor: DropTargetMonitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
    drop: (item: DragItem) => {
      const handleDrop = async () => {
        const filterPanels =
          chartControls?.properties?.[propKey]?.columnSecurity?.filterPanels?.[currentOperator];
        const existingFilters = filterPanels?.filters || [];
        const dataType = item.fieldData?.dataType?.toLowerCase();

        if (dataType !== 'text') {
          setAlert({
            message: 'Only text(string) fields can be dropped here!',
            severity: 'error',
          });
          return;
        }

        const isDuplicate = existingFilters.some(
          (f: any) => f.fieldName === item.fieldData?.fieldname,
        );

        if (isDuplicate) return;

        const filteredData = {
          dataType: item.fieldData?.dataType,
          displayName: item.fieldData?.displayname,
          fieldName: item.fieldData?.fieldname,
          source: uniqueId || '',
          sourceType: item.type || '',
          tableId: item.fieldData?.tableId,
          aggr: item.fieldData?.agg || '',
          calculationUUID: item.boxUUID,
          filterType: 'column_level_security_column',
          mapping: [],
          userSelection: [],
        };

        dispatch(updateMeasureDropFilters(propKey, filteredData, currentOperator));
        const newCard: MeasureCardData = {
          id: uniqueId,
          name: item.fieldData?.displayname || '',
        };

        setSelectedMapping([]);

        setCards([newCard]);
        setShowPopover(true);
      };

      handleDrop();
    },
  });

  const handleDelete = (id: string) => {
    dispatch(removeMeasureFieldCard(propKey, id, currentOperator));
    dispatch(removeMeasureMappingColumn(propKey, currentOperator, id));
    setCards((prevCards) => prevCards.filter((card) => card.id !== id));
    setSelectedMapping([]);
    // fetchAllData();
    dispatch(callQueryAPI(propKey, true));
  };

  return (
    <Box
      ref={drop}
      sx={{
        padding: 1,
        minHeight: '60px',
        border: isOver && canDrop? '1px dashed #2bb9bb' : 'none',
        backgroundColor: isOver && canDrop? 'rgba(43, 185, 187, 0.05)' : 'inherit',
      }}
    >
      {alert && (
        <NotificationDialog
          onCloseAlert={() => setAlert(null)}
          severity={alert.severity}
          testMessage={alert?.message}
          openAlert={!!alert}
        />
      )}

      {cards.length > 0 ? (
        <>
          {cards.map((card) => (
            <MeasureCard
              key={card.id}
              name={card.name}
              handleDelete={() => handleDelete(card.id)}
              showWarning={
                measureFields.length + extraMeasureFields.length !== savedMappings.length
              }
            />
          ))}
          <MeasureMapping
            measureFields={[...measureFields, ...extraMeasureFields]}
            measureDropFields={responseData}
            selectedMapping={selectedMapping}
            setSelectedMapping={setSelectedMapping}
            propKey={propKey}
            currentOperator={currentOperator}
            savedMappings={savedMappings}
          />
        </>
      ) : (
        <span>Drag and Drop a measure field or calculation</span>
      )}
    </Box>
  );
};

export default MeasureDropZone;
